/*
 * The candidate list research mode shows after a title search.
 *
 * A list rather than a best guess. Titles collide — "Attention Is All You Need"
 * matches the paper and a dozen that quote it — and picking one for the user
 * means spending a PDF download, a model read and a credit hold on the wrong
 * paper. Choosing is cheap; being wrong here is not.
 *
 * The empty state is the important one. arXiv holds preprints only, so no
 * results means "not on arXiv", which is a different claim from "no such paper"
 * and the only one we are entitled to make.
 */
import { byline, publishedYear, type PaperHit } from '@/features/generation/services/papersApi';

type Props = {
  results: PaperHit[];
  source: string;
  query: string;
  busyId: string | null;
  onPick: (hit: PaperHit) => void;
  onDismiss: () => void;
};

export function PaperCandidates({ results, source, query, busyId, onPick, onDismiss }: Props) {
  if (results.length === 0) {
    return (
      <div className="paper-candidates paper-candidates--empty" role="status">
        <p className="paper-candidates__empty-title">
          Nothing on {source === 'arxiv' ? 'arXiv' : source} matches “{query}”.
        </p>
        <p className="paper-candidates__empty-hint">
          Check the spelling first — search matches titles exactly, so one wrong
          letter finds nothing. If the title is right, arXiv holds preprints, so a
          paper published only in a journal is not there. Pasting the arXiv link
          always works.
        </p>
        <button type="button" className="paper-candidates__dismiss" onClick={onDismiss}>
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="paper-candidates">
      <div className="paper-candidates__head">
        <h2 className="paper-candidates__title">Which paper?</h2>
        <button type="button" className="paper-candidates__dismiss" onClick={onDismiss}>
          Back
        </button>
      </div>

      <ul className="paper-candidates__list">
        {results.map((hit) => {
          const busy = busyId === hit.arxivId;
          /* Any pick disables all of them: the next step is a 30-60s request,
             and two in flight would place two holds for one intent. */
          const disabled = busyId !== null;
          const year = publishedYear(hit.published);
          return (
            <li key={hit.arxivId}>
              <button
                type="button"
                className="paper-candidates__item"
                onClick={() => onPick(hit)}
                disabled={disabled}
                aria-busy={busy}
              >
                <span className="paper-candidates__item-title">{hit.title}</span>
                <span className="paper-candidates__item-meta">
                  {byline(hit.authors)}
                  {year && byline(hit.authors) ? ' · ' : ''}
                  {year}
                  {' · '}
                  {hit.arxivId}
                </span>
                {hit.summary && (
                  <span className="paper-candidates__item-summary">{hit.summary}</span>
                )}
                {busy && (
                  <span className="paper-candidates__item-status">
                    Reading the paper — this takes up to a minute…
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
